// staffConfigService.js - Firestore CRUD for the shared staff-config data model.
// Mirrors UlloorStyles@e7a5f9b:src/services/staffLeaveService.js and salonClosureService.js
// field shapes exactly, so mobile and web read/write the same salons/{salonId} data —
// single source of truth for staff count, roster, leave, and closures.
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Reschedule request nudge sent to a booking when a newly-added salon closure lands on its
 * booked date — same rescheduleRequest shape Dashboard.jsx's manual "Request Reschedule" writes
 * (handleConfirmRescheduleRequest), so the customer app renders it identically either way. The
 * booking itself is left confirmed; the customer picks a new time rather than losing the slot.
 */
export const SALON_CLOSURE_RESCHEDULE_MESSAGE =
  "The salon will be closed on your booked date, so we're unable to provide your service at " +
  'the scheduled time. We apologize for the inconvenience. Please reschedule your appointment';

/** Confirmed bookings for a salon that fall on the given local calendar date. */
const getConfirmedBookingsForDate = async (salonId, dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const startOfDay = new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
  const endOfDay = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();

  return getDocs(
    query(
      collection(db, 'bookings'),
      where('salonId', '==', salonId),
      where('bookingDate', '>=', startOfDay),
      where('bookingDate', '<=', endOfDay),
      where('bookingStatus', '==', 'confirmed')
    )
  );
};

/**
 * Count confirmed bookings across the given local calendar dates — lets the caller preview the
 * impact of newly-added closure dates (how many reschedule requests would go out) before the
 * salon commits to saving them.
 * @param {string} salonId
 * @param {string[]} dateStrs - YYYY-MM-DD, local
 * @returns {Promise<number>}
 */
export const countConfirmedBookingsOnDates = async (salonId, dateStrs) => {
  let total = 0;
  for (const dateStr of dateStrs) {
    const snapshot = await getConfirmedBookingsForDate(salonId, dateStr);
    total += snapshot.size;
  }
  return total;
};

/**
 * Send a reschedule request to every confirmed booking for a salon that falls on the given local
 * calendar date, instead of cancelling them outright. Bookings that already have a pending
 * reschedule request are left alone.
 * @param {string} salonId
 * @param {string} dateStr - YYYY-MM-DD, local
 * @returns {Promise<number>} number of reschedule requests sent
 */
const requestRescheduleForBookingsOnDate = async (salonId, dateStr) => {
  const snapshot = await getConfirmedBookingsForDate(salonId, dateStr);

  let requestedCount = 0;
  for (const bookingDoc of snapshot.docs) {
    if (bookingDoc.data()?.rescheduleRequest?.status === 'pending') continue;
    try {
      await updateDoc(bookingDoc.ref, {
        rescheduleRequest: {
          status: 'pending',
          message: SALON_CLOSURE_RESCHEDULE_MESSAGE,
          preferredTime: 'anytime',
          requestedAt: new Date().toISOString(),
          requestedBy: salonId,
          fulfilledAt: null,
          dismissedAt: null,
        },
        updatedAt: new Date().toISOString(),
      });
      requestedCount += 1;
    } catch (error) {
    }
  }

  return requestedCount;
};

/**
 * Format date to YYYY-MM-DD in LOCAL timezone (never toISOString(), which is UTC and can shift
 * the calendar day).
 */
export const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Client-generated roster entry id — not a Firestore doc id, just an array-element key. */
export const makeStaffId = () => `staff_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Max roster size per bucket (plain list, or each of men's/women's). Mirrors UlloorStyles@e7a5f9b:src/constants/staffConfig.js. */
export const MAX_STAFF_PER_BUCKET = 50;

/**
 * Build a named-staff roster from the persisted list if one exists, else synthesize "Staff 1",
 * "Staff 2", ... placeholders from the plain count. Salons configured before named rosters
 * existed only have a count saved (profile.staffCount / genderStaffCount.*.count), never an
 * explicit staffList — this keeps them usable everywhere a roster is read instead of showing
 * "no staff" until the owner re-saves the Staff Management screen.
 */
export const synthesizeStaffList = (existing, count, defaultServiceFor) => {
  if (Array.isArray(existing)) {
    return existing.map((s) => ({
      id: s.id || makeStaffId(),
      name: s.name || '',
      ...(defaultServiceFor ? { serviceFor: s.serviceFor || defaultServiceFor } : {}),
    }));
  }
  const n = Math.max(0, count || 0);
  return Array.from({ length: n }, (_, i) => ({
    id: makeStaffId(),
    name: `Staff ${i + 1}`,
    ...(defaultServiceFor ? { serviceFor: defaultServiceFor } : {}),
  }));
};

/**
 * The single roster of assignable staff for "who performed this service" pickers
 * (Complete Service, walk-in, Assign/Reassign Staff) — sourced from salons/{salonId}.profile,
 * the same staff-management data the mobile app and SalonStaffManagement page share.
 * Unisex salons split staff into men's/women's buckets; single-gender salons use one list.
 * Falls back to synthesized placeholders (see synthesizeStaffList) so salons that never saved
 * a named roster still show a pickable staff list here, matching what SalonStaffManagement
 * displays for them.
 */
export const getSalonStaffRoster = (salonData) => {
  const profile = salonData?.profile || {};
  const isUnisex = (profile.servicesFor || 'Unisex') === 'Unisex';
  const roster = isUnisex
    ? [
      ...synthesizeStaffList(profile.menStaffList, profile.genderStaffCount?.men?.count ?? 1, 'men'),
      ...synthesizeStaffList(profile.womenStaffList, profile.genderStaffCount?.women?.count ?? 1, 'women'),
    ]
    : synthesizeStaffList(profile.staffList, profile.staffCount ?? 1);
  return roster.filter((s) => s?.id && s.name?.trim());
};

// --- profile (staff count / roster) config -----------------------------------------------

/**
 * Partial update of salons/{salonId}.profile.* — caller builds the flat dot-path object, e.g.
 * { 'profile.staffCount': 3, 'profile.staffCountData': {...}, 'profile.staffList': [...] }.
 * A dot-path set replaces that field's value wholesale, matching how mobile saves the whole
 * staffCountData/genderStaffCount/staffList object each time, not a deep-merge.
 */
export const updateStaffProfileConfig = async (salonId, profileUpdates) => {
  await updateDoc(doc(db, 'salons', salonId), profileUpdates);
};

// --- staffLeaves subcollection -------------------------------------------------------------

/**
 * @param {string} salonId
 * @param {{date: string, leaveCount?: number, menLeaveCount?: number, womenLeaveCount?: number, staffIds?: string[], reason?: string}} leaveData
 */
export const setStaffLeave = async (salonId, leaveData) => {
  const { date, leaveCount, menLeaveCount, womenLeaveCount, staffIds, reason } = leaveData;
  const leaveRef = doc(db, 'salons', salonId, 'staffLeaves', date);

  await setDoc(leaveRef, {
    date,
    leaveCount: leaveCount ?? 0,
    menLeaveCount: menLeaveCount ?? 0,
    womenLeaveCount: womenLeaveCount ?? 0,
    ...(Array.isArray(staffIds) ? { staffIds } : {}),
    reason: reason || '',
    updatedAt: serverTimestamp(),
  }, { merge: true });

  return { id: date, ...leaveData };
};

/**
 * Batch upsert/delete leaves.
 * @param {string} salonId
 * @param {Object<string, object>} leavesData - date -> leave data (see setStaffLeave)
 * @param {string[]} datesToDelete
 */
export const saveStaffLeaves = async (salonId, leavesData, datesToDelete = []) => {
  const batch = writeBatch(db);
  const leavesRef = collection(db, 'salons', salonId, 'staffLeaves');

  datesToDelete.forEach((date) => {
    batch.delete(doc(leavesRef, date));
  });

  Object.entries(leavesData).forEach(([date, data]) => {
    batch.set(doc(leavesRef, date), {
      date,
      leaveCount: data.leaveCount ?? 0,
      menLeaveCount: data.menLeaveCount ?? 0,
      womenLeaveCount: data.womenLeaveCount ?? 0,
      ...(Array.isArray(data.staffIds) ? { staffIds: data.staffIds } : {}),
      reason: data.reason || '',
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });

  await batch.commit();
  return leavesData;
};

export const getStaffLeaveForDate = async (salonId, date) => {
  const dateStr = typeof date === 'string' ? date : formatDateLocal(date);
  const leaveDoc = await getDoc(doc(db, 'salons', salonId, 'staffLeaves', dateStr));
  return leaveDoc.exists() ? leaveDoc.data() : null;
};

export const getStaffLeavesInRange = async (salonId, startDate, endDate) => {
  const startStr = formatDateLocal(startDate);
  const endStr = formatDateLocal(endDate);

  const snapshot = await getDocs(
    query(
      collection(db, 'salons', salonId, 'staffLeaves'),
      where('date', '>=', startStr),
      where('date', '<=', endStr)
    )
  );

  const leaves = {};
  snapshot.forEach((d) => {
    leaves[d.id] = d.data();
  });
  return leaves;
};

/**
 * All leaves from today onward. Deliberately unindexed (full-collection getDocs + client-side
 * filter) — same as mobile — since a salon's staffLeaves subcollection is typically small and
 * this avoids needing a composite index for a simple date filter.
 */
export const getUpcomingStaffLeaves = async (salonId) => {
  try {
    const todayStr = formatDateLocal(new Date());
    const snapshot = await getDocs(collection(db, 'salons', salonId, 'staffLeaves'));

    const leaves = {};
    snapshot.forEach((d) => {
      const data = d.data();
      if (data.date >= todayStr) {
        leaves[d.id] = data;
      }
    });
    return leaves;
  } catch (error) {
    return {};
  }
};

export const deleteStaffLeave = async (salonId, date) => {
  const dateStr = typeof date === 'string' ? date : formatDateLocal(date);
  await deleteDoc(doc(db, 'salons', salonId, 'staffLeaves', dateStr));
};

// --- salonClosures subcollection ------------------------------------------------------------

export const getUpcomingSalonClosures = async (salonId) => {
  try {
    const todayStr = formatDateLocal(new Date());
    const snapshot = await getDocs(collection(db, 'salons', salonId, 'salonClosures'));

    const closures = {};
    snapshot.forEach((d) => {
      if (d.id >= todayStr) {
        closures[d.id] = d.data();
      }
    });
    return closures;
  } catch (error) {
    return {};
  }
};

export const getSalonClosuresInRange = async (salonId, startDate, endDate) => {
  const startStr = formatDateLocal(startDate);
  const endStr = formatDateLocal(endDate);
  const snapshot = await getDocs(collection(db, 'salons', salonId, 'salonClosures'));

  const out = {};
  snapshot.forEach((d) => {
    if (d.id >= startStr && d.id <= endStr) {
      out[d.id] = d.data();
    }
  });
  return out;
};

/**
 * Batch set/delete closure days, then send a reschedule request to any confirmed bookings that
 * fall on a *newly added* closure date — same nudge behavior as mobile's saveSalonClosures.
 * @param {string} salonId
 * @param {Object<string, {date: string}>} closuresByDate
 * @param {string[]} datesToDelete
 * @param {Object<string, *>} previousClosures - snapshot before edit, used to detect which dates are new
 * @returns {Promise<{ rescheduleRequestsSent: number }>}
 */
export const saveSalonClosures = async (salonId, closuresByDate, datesToDelete = [], previousClosures = {}) => {
  const col = collection(db, 'salons', salonId, 'salonClosures');
  const batch = writeBatch(db);

  datesToDelete.forEach((date) => {
    batch.delete(doc(col, date));
  });

  Object.keys(closuresByDate).forEach((date) => {
    batch.set(doc(col, date), {
      date,
      closed: true,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });

  await batch.commit();

  const previousKeys = new Set(Object.keys(previousClosures || {}));
  const addedDates = Object.keys(closuresByDate).filter((date) => !previousKeys.has(date));

  let rescheduleRequestsSent = 0;
  for (const dateStr of addedDates) {
    rescheduleRequestsSent += await requestRescheduleForBookingsOnDate(salonId, dateStr);
  }

  return { rescheduleRequestsSent };
};
